import { useEffect, useState } from "react";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startGetAdsCampaign,
  startGetDetailedCampaignReport,
} from "../../actions/adsCampaigns";
import Loader from "../../components/Loaders/Loader";
import { LOADING_INFORMATION } from "../../i18n/keysTranslations";
import Box from "@mui/material/Box";
import _ from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import differenceInWeeks from "date-fns/differenceInWeeks";
import addWeeks from "date-fns/addWeeks";
import subDays from "date-fns/subDays";
import ContainerStatistics from "./components/ContainerStatistics";
import {
  getCostByMainCurrency as getCostByMainCurrencyFunc,
  getFromStatisticsAdsCampaign,
  sumStatisticsAdsCampaign,
} from "../../services/adsCampaigns";
import { Helmet } from "react-helmet";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  GOOGLE,
  ROUTE_CAMPAIGNS,
} from "../../utils/constants";
import {
  getAdsCampaignDemoDateRange,
  isAdsCampaignDemo,
} from "../../utils/adsCampaignDemoDateRange";

const getEndDateTime = (startDateTime) => {
  const now = new Date();
  const differenceWeeks = differenceInWeeks(now, startDateTime);
  if (differenceWeeks === 0) return addWeeks(startDateTime, 1);
  if (differenceWeeks >= 1 && differenceWeeks <= 4)
    return addWeeks(startDateTime, differenceWeeks);

  return addWeeks(startDateTime, 4);
};

const AdsCampaignStatistics = () => {
  const [campaign, setCampaign] = useState({});
  const [reportConfiguration, setReportConfiguration] = useState({});
  const [reportDateLimits, setReportDateLimits] = useState({});
  const [isGeneratedReport, setIsGeneratedReport] = useState(false);
  const [isEmptyReport, setIsEmptyReport] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(true);
  const [isOpenChat, setIsOpenChat] = useState(true);
  const [currenciesConfiguration, setCurrenciesConfiguration] = useState({});

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const campaignID = params.get("campaignID");
  const { userID } = dispatch(getPathDatabaseByRole());
  const { theme } = useSelectorApp((state) => state.ui);

  const methods = useForm({
    defaultValues: {
      dateRange: {
        startDateTime: null,
        endDateTime: null,
      },
      breakdown: "weekly",
    },
  });

  const { setValue, getValues } = methods;

  useFocusErrorForm(methods.formState);

  const getInitialData = async () => {
    setIsLoading(true);
    const campaignObject = await dispatch(startGetAdsCampaign(campaignID));

    if (!campaignObject) {
      navigate(ROUTE_CAMPAIGNS);
    }

    setCampaign(campaignObject);

    const isDemoCampaign = isAdsCampaignDemo(campaignID);
    const newReportRange = isDemoCampaign
      ? getAdsCampaignDemoDateRange()
      : {
          startDateTime: campaignObject.firstActiveTime
            ? new Date(campaignObject.firstActiveTime)
            : new Date(),
          endDateTime: getEndDateTime(
            campaignObject.firstActiveTime
              ? new Date(campaignObject.firstActiveTime)
              : new Date()
          ),
        };
    setReportConfiguration(newReportRange);
    setValue("dateRange", newReportRange);

    // Calculate date limits for the selector (minimum start and maximum end)
    const computeCampaignMaxEndDate = (campaignParam) => {
      const today = new Date();
      try {
        const platforms = campaignParam?.platforms || [];
        const adsGroups = Array.isArray(campaignParam?.adsGroups)
          ? campaignParam.adsGroups
          : [];

        // If it has a Google sub-campaign -> limit today (Google always daily budget)
        if (platforms.includes(GOOGLE)) return today;

        // If there is at least one adgroup with a daily budget -> limit today
        const hasDailyBudget = adsGroups.some(
          (ag) => ag?.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
        );
        if (hasDailyBudget) return today;

        // If it doesn't have Google and all budgets are total -> choose the furthest date
        const totalGroups = adsGroups.filter(
          (ag) => ag?.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION
        );
        if (totalGroups.length > 0 && totalGroups.length === adsGroups.length) {
          const endDates = totalGroups
            .map((ag) => (ag?.endDate ? new Date(ag.endDate) : null))
            .filter((d) => d && !isNaN(d.getTime()));
          if (endDates.length > 0) {
            return new Date(Math.max(...endDates.map((d) => d.getTime())));
          }
        }

        // Fallback: today
        return today;
      } catch (e) {
        return today;
      }
    };

    const demoDateRange = isDemoCampaign ? getAdsCampaignDemoDateRange() : null;
    const minStartDate = isDemoCampaign
      ? demoDateRange.startDateTime
      : campaignObject.firstActiveTime
        ? new Date(campaignObject.firstActiveTime)
        : null;
    const maxStartDate = isDemoCampaign
      ? demoDateRange.endDateTime
      : new Date();
    const maxEndDate = isDemoCampaign
      ? demoDateRange.endDateTime
      : computeCampaignMaxEndDate(campaignObject);

    setReportDateLimits({
      minStartDate,
      maxStartDate,
      maxEndDate,
    });

    getDetailedCampaignReport();

    setIsLoading(false);
  };

  const getDetailedCampaignReport = async () => {
    setIsEmptyReport(false);
    setIsGeneratingReport(true);
    setIsGeneratedReport(true);

    const dateRange = getValues("dateRange");

    const response = await dispatch(
      startGetDetailedCampaignReport({
        campaignID,
        breakdown: "daily", // forced weekly to be able to switch between weekly / month in the UI
        startDateTime: new Date(dateRange.startDateTime).getTime(),
        endDateTime: new Date(dateRange.endDateTime).getTime(),
        getPreviousPeriod: true,
      })
    );
    if (response.ok) {
      const {
        statistics,
        mainCurrency,
        currenciesTax,
        statisticsPreviousPeriod,
      } = response.data;
      if (_.isEmpty(statistics)) {
        setIsEmptyReport(true);
        setIsGeneratingReport(false);
        return;
      }
      setCampaign((prev) => ({
        ...prev,
        historicalStatistics: statistics,
        statisticsPreviousPeriod,
      }));
      const newReportRange = {
        startDateTime: new Date(dateRange.startDateTime),
        endDateTime: new Date(dateRange.endDateTime),
      };
      setValue("dateRange", newReportRange);
      setReportConfiguration(newReportRange);
      setCurrenciesConfiguration({
        mainCurrency,
        currenciesTax,
      });
    } else {
      setIsEmptyReport(true);
    }
    setIsGeneratingReport(false);
  };

  const getCostByMainCurrency = ({ cost, currency }) => {
    return getCostByMainCurrencyFunc({
      cost,
      costCurrency: currency,
      mainCurrency: currenciesConfiguration.mainCurrency,
      currenciesTax: currenciesConfiguration.currenciesTax,
    });
  };

  const getAggregatedMetrics = ({
    forcedBreakdown = null,
    includePreviousPeriod = false,
  } = {}) => {
    const breakdown = forcedBreakdown || getValues("breakdown");
    const { historical } = getFromStatisticsAdsCampaign(campaign);
    const previousPeriodData = includePreviousPeriod
      ? campaign?.statisticsPreviousPeriod?.historical
      : null;
    let batchSize = 1;
    let dataProcessed = {};
    let previousPeriodProcessed = {};

    if (breakdown === "weekly") {
      batchSize = 7;
    } else if (breakdown === "monthly") {
      batchSize = 28;
    }

    if (breakdown === "weekly") {
      const dataKeys = Object.keys(historical);
      const batches = [];
      let remaining = [...dataKeys];

      while (remaining.length > 0) {
        if (remaining.length >= batchSize) {
          const batch = remaining.slice(-batchSize);
          remaining = remaining.slice(0, -batchSize);
          batches.unshift(batch);
        } else {
          batches.unshift(remaining);
          remaining = [];
        }
      }

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        let snapshot = {};
        for (let j = 0; j < batch.length; j++) {
          const dataSnapshot = historical?.[batch?.[j]];
          if (!dataSnapshot) continue;

          if (j === 0) {
            snapshot.startDateTime = historical?.[batch?.[j]]?.startDateTime;
          }
          if (j === batch.length - 1) {
            snapshot.endDateTime = historical?.[batch?.[j]]?.endDateTime;
          }

          const platformsStatistics = Object.keys(dataSnapshot).filter(
            (key) => key !== "startDateTime" && key !== "endDateTime"
          );

          for (const platform of platformsStatistics) {
            snapshot[platform] = {
              campaign: sumStatisticsAdsCampaign({
                current: snapshot?.[platform]?.campaign,
                addition: dataSnapshot?.[platform]?.campaign,
              }),
            };
          }
        }

        if (batch.length < batchSize && snapshot.endDateTime) {
          const endDate = new Date(snapshot.endDateTime);
          const startDate = subDays(endDate, batchSize - 1);
          snapshot.startDateTime = startDate.getTime();
        }

        dataProcessed[batch[0]] = snapshot;
      }

      if (previousPeriodData) {
        const previousDataKeys = Object.keys(previousPeriodData);
        const previousBatches = [];
        let previousRemaining = [...previousDataKeys];

        while (previousRemaining.length > 0) {
          if (previousRemaining.length >= batchSize) {
            const batch = previousRemaining.slice(-batchSize);
            previousRemaining = previousRemaining.slice(0, -batchSize);
            previousBatches.unshift(batch);
          } else {
            previousBatches.unshift(previousRemaining);
            previousRemaining = [];
          }
        }

        for (let i = 0; i < previousBatches.length; i++) {
          const batch = previousBatches[i];
          let snapshot = {};
          for (let j = 0; j < batch.length; j++) {
            const dataSnapshot = previousPeriodData?.[batch?.[j]];
            if (!dataSnapshot) continue;

            if (j === 0) {
              snapshot.startDateTime =
                previousPeriodData?.[batch?.[j]]?.startDateTime;
            }
            if (j === batch.length - 1) {
              snapshot.endDateTime =
                previousPeriodData?.[batch?.[j]]?.endDateTime;
            }

            const platformsStatistics = Object.keys(dataSnapshot).filter(
              (key) => key !== "startDateTime" && key !== "endDateTime"
            );

            for (const platform of platformsStatistics) {
              snapshot[platform] = {
                campaign: sumStatisticsAdsCampaign({
                  current: snapshot?.[platform]?.campaign,
                  addition: dataSnapshot?.[platform]?.campaign,
                }),
              };
            }
          }

          if (batch.length < batchSize && snapshot.endDateTime) {
            const endDate = new Date(snapshot.endDateTime);
            const startDate = subDays(endDate, batchSize - 1);
            snapshot.startDateTime = startDate.getTime();
          }

          previousPeriodProcessed[batch[0]] = snapshot;
        }
      }
    } else if (breakdown === "monthly") {
      const dataKeys = Object.keys(historical);
      const batches = [];
      let remaining = [...dataKeys];

      while (remaining.length > 0) {
        if (remaining.length >= batchSize) {
          const batch = remaining.slice(-batchSize);
          remaining = remaining.slice(0, -batchSize);
          batches.unshift(batch);
        } else {
          batches.unshift(remaining);
          remaining = [];
        }
      }

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        let snapshot = {};
        for (let j = 0; j < batch.length; j++) {
          const dataSnapshot = historical?.[batch?.[j]];
          if (!dataSnapshot) continue;

          if (j === 0) {
            snapshot.startDateTime = historical?.[batch?.[j]]?.startDateTime;
          }
          if (j === batch.length - 1) {
            snapshot.endDateTime = historical?.[batch?.[j]]?.endDateTime;
          }

          const platformsStatistics = Object.keys(dataSnapshot).filter(
            (key) => key !== "startDateTime" && key !== "endDateTime"
          );

          for (const platform of platformsStatistics) {
            snapshot[platform] = {
              campaign: sumStatisticsAdsCampaign({
                current: snapshot?.[platform]?.campaign,
                addition: dataSnapshot?.[platform]?.campaign,
              }),
            };
          }
        }

        if (batch.length < batchSize && snapshot.endDateTime) {
          const endDate = new Date(snapshot.endDateTime);
          const startDate = subDays(endDate, batchSize - 1);
          snapshot.startDateTime = startDate.getTime();
        }

        dataProcessed[batch[0]] = snapshot;
      }

      if (previousPeriodData) {
        const previousDataKeys = Object.keys(previousPeriodData);
        const previousBatches = [];
        let previousRemaining = [...previousDataKeys];

        while (previousRemaining.length > 0) {
          if (previousRemaining.length >= batchSize) {
            const batch = previousRemaining.slice(-batchSize);
            previousRemaining = previousRemaining.slice(0, -batchSize);
            previousBatches.unshift(batch);
          } else {
            previousBatches.unshift(previousRemaining);
            previousRemaining = [];
          }
        }

        for (let i = 0; i < previousBatches.length; i++) {
          const batch = previousBatches[i];
          let snapshot = {};
          for (let j = 0; j < batch.length; j++) {
            const dataSnapshot = previousPeriodData?.[batch?.[j]];
            if (!dataSnapshot) continue;

            if (j === 0) {
              snapshot.startDateTime =
                previousPeriodData?.[batch?.[j]]?.startDateTime;
            }
            if (j === batch.length - 1) {
              snapshot.endDateTime =
                previousPeriodData?.[batch?.[j]]?.endDateTime;
            }

            const platformsStatistics = Object.keys(dataSnapshot).filter(
              (key) => key !== "startDateTime" && key !== "endDateTime"
            );

            for (const platform of platformsStatistics) {
              snapshot[platform] = {
                campaign: sumStatisticsAdsCampaign({
                  current: snapshot?.[platform]?.campaign,
                  addition: dataSnapshot?.[platform]?.campaign,
                }),
              };
            }
          }

          if (batch.length < batchSize && snapshot.endDateTime) {
            const endDate = new Date(snapshot.endDateTime);
            const startDate = subDays(endDate, batchSize - 1);
            snapshot.startDateTime = startDate.getTime();
          }

          previousPeriodProcessed[batch[0]] = snapshot;
        }
      }
    } else {
      dataProcessed = historical;
      previousPeriodProcessed = previousPeriodData;
    }

    return {
      current: dataProcessed,
      previous: previousPeriodProcessed,
    };
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [campaignID, userID]);

  if (isLoading) {
    return (
      <Loader fullWidth hasMessage={true} message={t(LOADING_INFORMATION)} />
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Helmet>
        <title>{`${theme.nameBusiness} - Campaign Report`}</title>
      </Helmet>
      <FormProvider
        {...methods}
        campaign={campaign}
        campaignID={campaignID}
        reportConfiguration={reportConfiguration}
        reportDateLimits={reportDateLimits}
        isGeneratingReport={isGeneratingReport}
        isEmptyReport={isEmptyReport}
        isOpenChat={isOpenChat}
        onChangeShowChat={setIsOpenChat}
        isGeneratedReport={isGeneratedReport}
        getDetailedCampaignReport={getDetailedCampaignReport}
        getCostByMainCurrency={getCostByMainCurrency}
        getAggregatedMetrics={getAggregatedMetrics}
      >
        <ContainerStatistics />
      </FormProvider>
    </Box>
  );
};

export default AdsCampaignStatistics;
